<template>
  <div class="flex gap-2">
    <div class="p-4 flex flex-col gap-4.5 w-65 glass-frosted rounded-3xl">
      <div>
        <p class="text-l-medium text-black-white">{{ $t('performance.rfStrategy.title') }}</p>
      </div>
      <div class="flex flex-col gap-1">
        <div class="relative p-1 flex items-center gap-0.5 bg-key rounded-full">
          <!-- 滑动指示器 -->
          <div
            class="absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-full"
            :class="[switchColor, motionClass]"
            :style="{ transform: `translateX(${rfBoostSwitch === 0 ? '0%' : '100%'})` }"
          />
          <!-- 选项 -->
          <div
            class="relative z-1 py-1 flex-1 flex justify-center text-m-medium text-center cursor-pointer"
            :class="[
              motionColorClass,
              rfBoostSwitch === 0 ? 'text-white' : 'text-black-white hover:text-text-glass-hover',
            ]"
            @click="emit('update:rfBoostSwitch', 0)"
          >
            {{ $t('performance.rfStrategy.smart') }}
          </div>
          <div
            class="relative z-1 py-1 flex-1 flex justify-center text-m-medium text-center cursor-pointer"
            :class="[
              motionColorClass,
              rfBoostSwitch === 1 ? 'text-white' : 'text-black-white hover:text-text-glass-hover',
            ]"
            @click="emit('update:rfBoostSwitch', 1)"
          >
            {{ $t('performance.rfStrategy.fullPower') }}
          </div>
        </div>
        <p class="text-s-medium text-black-white">
          {{
            rfBoostSwitch === 0
              ? $t('performance.rfStrategy.smartDesc')
              : $t('performance.rfStrategy.fullPowerDesc')
          }}
        </p>
      </div>
    </div>
    <div class="p-4 flex flex-col gap-4.5 min-w-68 glass-frosted rounded-3xl">
      <div class="flex items-center justify-between">
        <p class="text-l-medium text-black-white">{{ $t('performance.battery.title') }}</p>
        <Tooltip :content="t('performance.battery.tooltip')">
          <Icon icon="Function-Wireframe-Description" class="size-4 text-black-white" />
        </Tooltip>
      </div>
      <div class="pb-2 flex items-center justify-between gap-4">
        <div class="text-s-medium text-black-white">
          <p>{{ $t('performance.battery.currentConfig') }}</p>
          <p>{{ $t('performance.battery.fullCharge') }}</p>
        </div>
        <div class="deco-m-semibold font-[Barlow] text-green-active tabular-nums">
          {{ batteryLife }}
        </div>
      </div>
    </div>
    <!-- 通讯协议设置 -->
    <div
      v-if="enabledRfPacketType"
      class="p-4 flex flex-col gap-4.5 w-65 glass-frosted rounded-3xl"
    >
      <div>
        <p class="text-l-medium text-black-white">{{ $t('performance.rfPacket.title') }}</p>
      </div>
      <div class="relative p-1 flex items-center gap-0.5 bg-key rounded-full h-9">
        <div
          class="absolute top-1 bottom-1 w-[calc(50%-4px)] bg-green-active rounded-full"
          :class="motionTransformClass"
          :style="{ transform: `translateX(${rfPacketType === 0 ? '0%' : '100%'})` }"
        />
        <div
          class="relative z-1 py-1 flex-1 flex justify-center text-m-medium text-center cursor-pointer"
          :class="[
            motionColorClass,
            rfPacketType === 0 ? 'text-white' : 'text-black-white hover:text-text-glass-hover',
          ]"
          @click="emit('update:rfPacketType', 0)"
        >
          {{ $t('performance.rfPacket.turbo') }}
        </div>
        <div
          class="relative z-1 py-1 flex-1 flex justify-center text-m-medium text-center cursor-pointer"
          :class="[
            motionColorClass,
            rfPacketType === 2 ? 'text-white' : 'text-black-white hover:text-text-glass-hover',
          ]"
          @click="emit('update:rfPacketType', 2)"
        >
          {{ $t('performance.rfPacket.initial') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, shallowRef, watch } from 'vue'
  import { Icon, Tooltip } from '@rapoo/element'
  import {
    DeviceError,
    DeviceErrorCode,
    type RfBoostSwitch,
    type RfPacketType,
    type ReportRate,
    type SensorMode,
    type KeyScanRate,
  } from '@rapoo/device-sdk'
  import { getBatteryLife } from '../utils/batteryLife'
  import { useDeviceStore, useLocaleStore } from '@/stores'

  const { t } = useLocaleStore()
  const deviceStore = useDeviceStore()

  const props = defineProps<{
    enabledRfPacketType: boolean
    rfBoostSwitch: RfBoostSwitch
    rfPacketType: RfPacketType
    reportRate: ReportRate
    sensorMode: SensorMode
    keyScanRate: KeyScanRate
    /** 外部同步是否过渡。页面回读期间为 false。 */
    animate?: boolean
  }>()

  const motionClass = computed(() =>
    props.animate ? 'transition-all duration-300 ease-in-out' : '',
  )
  const motionColorClass = computed(() => (props.animate ? 'transition-colors duration-300' : ''))
  const motionTransformClass = computed(() =>
    props.animate ? 'transition-transform duration-300 ease-in-out' : '',
  )

  const emit = defineEmits<{
    'update:rfBoostSwitch': [value: RfBoostSwitch]
    'update:rfPacketType': [value: RfPacketType]
  }>()

  /**
   * unknown：尚未判定，避免 Telink 鼠标先闪一下查表默认值。
   * device：`getBatteryRuntime` 成功，展示设备返回的最低/最高小时。
   * table：芯片未实现该方法，继续走 manifest 续航表。
   */
  const runtimeMode = shallowRef<'unknown' | 'device' | 'table'>('unknown')
  const deviceRuntimeText = shallowRef<string | null>(null)
  let runtimeRequestId = 0

  async function loadDeviceRuntime() {
    const battery = deviceStore.currentDevice?.features.battery
    if (!battery) {
      runtimeMode.value = 'table'
      deviceRuntimeText.value = null
      return
    }

    const requestId = ++runtimeRequestId
    try {
      const runtime = await battery.getBatteryRuntime()
      if (requestId !== runtimeRequestId) return
      runtimeMode.value = 'device'
      deviceRuntimeText.value =
        runtime.min === runtime.max ? `${runtime.min}h` : `${runtime.min}-${runtime.max}h`
    } catch (error) {
      if (requestId !== runtimeRequestId) return
      if (error instanceof DeviceError && error.code === DeviceErrorCode.NOT_SUPPORTED) {
        runtimeMode.value = 'table'
        deviceRuntimeText.value = null
        return
      }
      console.warn('[DeviceSettings] Failed to read battery runtime:', error)
    }
  }

  watch(
    () =>
      [
        deviceStore.currentDevice,
        props.reportRate,
        props.sensorMode,
        props.rfBoostSwitch,
        props.keyScanRate,
        props.rfPacketType,
      ] as const,
    () => {
      void loadDeviceRuntime()
    },
    { immediate: true },
  )

  const batteryLife = computed(() => {
    if (runtimeMode.value === 'device') return deviceRuntimeText.value ?? ''
    if (runtimeMode.value === 'table') {
      return getBatteryLife(
        props.reportRate,
        props.sensorMode,
        props.rfBoostSwitch,
        props.keyScanRate,
        props.rfPacketType,
      )
    }
    return ''
  })

  const switchColor = computed(() => {
    switch (props.sensorMode) {
      case 0:
        return 'bg-brand-blue1'

      case 1:
        return 'bg-green-active'

      case 2:
        return 'bg-purple-active'

      case 3:
      case 4:
        return 'bg-brand-orange'

      case 5:
        return 'bg-brand-red'

      default:
        return 'bg-brand-red'
    }
  })
</script>

<style scoped></style>
